import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Search,
  Plus,
  X,
  Pencil,
  Trash2,
  Check,
  AlertTriangle,
  Tag as TagIcon,
  ChevronLeft,
  Users,
  Building2,
  CheckSquare
} from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToastNotification } from '@/contexts/ToastContext';

// Color wheel component
import { Wheel } from '@uiw/react-color';

// 12 predefined colors
const PREDEFINED_COLORS = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Lime', value: '#84cc16' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tag type configuration
const TAG_TYPES = [
  { value: 'contacts', label: 'Contacts Tags', icon: Users, color: 'blue' },
  { value: 'organizations', label: 'Organizations Tags', icon: Building2, color: 'purple' },
  { value: 'tasks', label: 'Tasks Tags', icon: CheckSquare, color: 'green' }
];

// Color Picker Component - Fixed width with smooth height transition
const ColorPicker = ({ color, onChange, onClose }) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customColor, setCustomColor] = useState(color || '#3b82f6');
  const [hexInput, setHexInput] = useState(color || '#3b82f6');

  const handlePredefinedSelect = (colorValue) => {
    onChange(colorValue);
    onClose();
  };

  // Handle color wheel change
  const handleWheelChange = (colorObj) => {
    const hex = colorObj.hex;
    setCustomColor(hex);
    setHexInput(hex);
  };

  const handleHexInputChange = (e) => {
    let value = e.target.value;
    // Ensure it starts with #
    if (!value.startsWith('#')) {
      value = '#' + value;
    }
    setHexInput(value);
    // Validate hex format and update wheel
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      setCustomColor(value);
    }
  };

  const applyCustomColor = () => {
    if (/^#[0-9A-Fa-f]{6}$/.test(hexInput)) {
      onChange(hexInput);
      onClose();
    }
  };

  return (
    <div className="w-[220px] overflow-hidden">
      <motion.div
        initial={false}
        animate={{ height: showCustom ? 'auto' : 'auto' }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="p-3"
      >
        <AnimatePresence mode="wait">
          {!showCustom ? (
            <motion.div
              key="presets"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
              className="space-y-3"
            >
              <div className="grid grid-cols-6 gap-2">
                {PREDEFINED_COLORS.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => handlePredefinedSelect(c.value)}
                    className={`w-7 h-7 rounded-md border-2 transition-all hover:scale-110 ${
                      color === c.value ? 'border-white ring-2 ring-white/20' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: c.value }}
                    title={c.name}
                  />
                ))}
              </div>
              <div className="pt-2 border-t border-white/10">
                <button
                  onClick={() => setShowCustom(true)}
                  className="w-full text-sm text-zinc-400 hover:text-white py-1.5 rounded-md hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
                >
                  <div className="w-4 h-4 rounded border border-white/20 bg-gradient-to-br from-red-500 via-green-500 to-blue-500" />
                  Custom Color
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="custom"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.15 }}
              className="space-y-3"
            >
              {/* Back button */}
              <button
                onClick={() => setShowCustom(false)}
                className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors group"
              >
                <ChevronLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
                Back to presets
              </button>
              
              {/* Color Wheel - Centered */}
              <div className="flex justify-center py-1">
                <Wheel
                  color={customColor}
                  onChange={handleWheelChange}
                  width={160}
                  height={160}
                />
              </div>
              
              {/* Color Preview + HEX Input Row */}
              <div className="flex items-center gap-3 pt-2 border-t border-white/10">
                {/* Color Preview */}
                <div
                  className="w-10 h-10 rounded-lg border border-white/10 flex-shrink-0 shadow-inner"
                  style={{ backgroundColor: customColor }}
                />
                
                {/* HEX Input */}
                <div className="flex-1 space-y-1">
                  <label className="text-xs text-zinc-500 font-medium">HEX Code</label>
                  <Input
                    value={hexInput}
                    onChange={handleHexInputChange}
                    placeholder="#000000"
                    className="bg-[#0a0a0a] border-white/10 text-zinc-200 font-mono text-sm h-8"
                    maxLength={7}
                  />
                </div>
              </div>
              
              {/* Apply Button */}
              <Button
                onClick={applyCustomColor}
                size="sm"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-8"
              >
                <Check size={14} className="mr-1.5" />
                Apply Color
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

// Tag Card Component (for grid display) - Memoized for performance
const TagCard = React.memo(({ tag, onEdit, onDelete, isEditing, onSave, onCancel, editData, setEditData }) => {
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  if (isEditing) {
    return (
      <motion.div
        className="bg-[#0f1115] border-2 border-blue-500/50 rounded-lg p-4 space-y-3"
      >
        <div className="flex items-start gap-3">
          <Popover open={colorPickerOpen} onOpenChange={setColorPickerOpen}>
            <PopoverTrigger asChild>
              <button
                className="w-10 h-10 rounded-lg border border-white/20 flex-shrink-0 hover:border-white/40 transition-colors"
                style={{ backgroundColor: editData.color }}
                title="Change color"
              />
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#0f1115] border-white/10" align="start" side="top" sideOffset={8}>
              <ColorPicker
                color={editData.color}
                onChange={(newColor) => setEditData({ ...editData, color: newColor })}
                onClose={() => setColorPickerOpen(false)}
              />
            </PopoverContent>
          </Popover>
          
          <div className="flex-1 space-y-2">
            <Input
              value={editData.name}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              placeholder="Tag name"
              className="bg-[#0a0a0a] border-white/10 text-zinc-200 text-sm font-medium"
              autoFocus
            />
            <Input
              value={editData.description}
              onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              placeholder="Description (optional)"
              className="bg-[#0a0a0a] border-white/10 text-zinc-400 text-xs"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/6">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="text-zinc-400 hover:text-white hover:bg-white/5 h-8 px-3"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onSave}
            disabled={!editData.name.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-3"
          >
            <Check size={14} className="mr-1" />
            Save
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-[#0f1115] border border-white/6 rounded-lg p-4 hover:border-white/10 transition-all group"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-lg flex-shrink-0"
          style={{ backgroundColor: tag.color }}
        />
        
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-zinc-200 truncate">{tag.name}</h3>
          <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{tag.description || 'No description'}</p>
        </div>
        
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(tag)}
            className="p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-white/5 rounded-md transition-colors"
            title="Edit tag"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={() => onDelete(tag)}
            className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
            title="Delete tag"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      
      <div className="mt-3 pt-3 border-t border-white/6">
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-500">Usage</span>
          <span className="text-zinc-400">
            {tag.usage_count} item{tag.usage_count !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </motion.div>
  );
});

// Add New Tag Card Component - Memoized for performance
const AddTagCard = React.memo(({ onAdd }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [newTag, setNewTag] = useState({
    name: '',
    color: '#3b82f6',
    description: ''
  });

  const handleSave = async () => {
    if (newTag.name.trim()) {
      try {
        await onAdd(newTag);
        setNewTag({ name: '', color: '#3b82f6', description: '' });
        setIsAdding(false);
      } catch (error) {
        console.error('Failed to create tag:', error);
      }
    }
  };

  const handleCancel = () => {
    setNewTag({ name: '', color: '#3b82f6', description: '' });
    setIsAdding(false);
  };

  if (isAdding) {
    return (
      <motion.div
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0f1115] border-2 border-dashed border-blue-500/50 rounded-lg p-4 space-y-3"
      >
        <div className="flex items-start gap-3">
          <Popover open={colorPickerOpen} onOpenChange={setColorPickerOpen}>
            <PopoverTrigger asChild>
              <button
                className="w-10 h-10 rounded-lg border border-white/20 flex-shrink-0 hover:border-white/40 transition-colors"
                style={{ backgroundColor: newTag.color }}
                title="Choose color"
              />
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#0f1115] border-white/10" align="start" side="top" sideOffset={8}>
              <ColorPicker
                color={newTag.color}
                onChange={(newColor) => setNewTag({ ...newTag, color: newColor })}
                onClose={() => setColorPickerOpen(false)}
              />
            </PopoverContent>
          </Popover>
          
          <div className="flex-1 space-y-2">
            <Input
              value={newTag.name}
              onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
              placeholder="Tag name"
              className="bg-[#0a0a0a] border-white/10 text-zinc-200 text-sm font-medium"
              autoFocus
            />
            <Input
              value={newTag.description}
              onChange={(e) => setNewTag({ ...newTag, description: e.target.value })}
              placeholder="Description (optional)"
              className="bg-[#0a0a0a] border-white/10 text-zinc-400 text-xs"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/6">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-zinc-400 hover:text-white hover:bg-white/5 h-8 px-3"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!newTag.name.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-3"
          >
            <Check size={14} className="mr-1" />
            Create Tag
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.button
      onClick={() => setIsAdding(true)}
      className="bg-[#0f1115] border-2 border-dashed border-white/10 rounded-lg p-4 hover:border-white/20 hover:bg-white/[0.02] transition-all flex items-center justify-center gap-2 text-zinc-500 hover:text-zinc-300 min-h-[120px]"
    >
      <Plus size={20} />
      <span className="font-medium">Add Tag</span>
    </motion.button>
  );
});

// Delete Confirmation Dialog - Memoized for performance
const DeleteDialog = React.memo(({ tag, open, onClose, onConfirm }) => {
  if (!tag) return null;
  
  const isInUse = tag.usage_count > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isInUse && <AlertTriangle size={20} className="text-amber-400" />}
            Delete Tag
          </DialogTitle>
          <DialogDescription>
            {isInUse ? (
              <span className="text-amber-400">
                This tag is currently used by {tag.usage_count} item{tag.usage_count !== 1 ? 's' : ''}. Deleting it will remove the tag from all associated records.
              </span>
            ) : (
              <span>Are you sure you want to delete the tag &ldquo;{tag.name}&rdquo;?</span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex items-center gap-3 py-4">
          <div
            className="w-10 h-10 rounded-lg"
            style={{ backgroundColor: tag.color }}
          />
          <div>
            <p className="font-medium text-zinc-200">{tag.name}</p>
            <p className="text-xs text-zinc-500">{tag.description || 'No description'}</p>
          </div>
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-zinc-400 hover:text-white hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className={isInUse 
              ? 'bg-amber-600 hover:bg-amber-700 text-white' 
              : 'bg-red-600 hover:bg-red-700 text-white'
            }
          >
            <Trash2 size={14} className="mr-1" />
            {isInUse ? 'Delete Anyway' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

// Main Page Component
const SegmentsTags = () => {
  const toast = useToastNotification();
  const [tags, setTags] = useState([]);
  const [selectedType, setSelectedType] = useState('contacts');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [editingTagId, setEditingTagId] = useState(null);
  const [editData, setEditData] = useState({ name: '', color: '', description: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper function to show toast (uses global context)
  const showToast = (type, message, addonText) => {
    toast[type === 'gray' ? 'gray' : type](message, addonText);
  };
  
  // Cache for tags by type
  const tagsCache = useRef({});
  const searchTimeout = useRef(null);

  // Debounce search query
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchQuery]);

  const fetchTags = useCallback(async () => {
    // Check cache first
    if (tagsCache.current[selectedType]) {
      setTags(tagsCache.current[selectedType]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/tags`, {
        params: { tag_type: selectedType },
        withCredentials: true
      });
      
      // Cache the results
      tagsCache.current[selectedType] = response.data;
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedType]);

  // Fetch tags from API when type changes (with caching)
  useEffect(() => {
    fetchTags();
  }, [selectedType, fetchTags]);

  // Filter tags based on debounced search
  const filteredTags = useMemo(() => {
    if (!debouncedSearch) return tags;
    const query = debouncedSearch.toLowerCase();
    return tags.filter(tag =>
      tag.name.toLowerCase().includes(query) ||
      tag.description?.toLowerCase().includes(query)
    );
  }, [tags, debouncedSearch]);

  // Handle edit
  const handleEdit = (tag) => {
    setEditingTagId(tag.id);
    setEditData({
      name: tag.name,
      color: tag.color,
      description: tag.description || ''
    });
  };

  // Handle save edit with optimistic update
  const handleSaveEdit = async () => {
    if (editData.name.trim()) {
      // Store old tag for rollback
      const oldTag = tags.find(t => t.id === editingTagId);
      
      // Optimistic update
      const optimisticTag = {
        ...oldTag,
        name: editData.name,
        color: editData.color,
        description: editData.description,
        updatedAt: new Date().toISOString()
      };
      
      setTags(prev => prev.map(tag =>
        tag.id === editingTagId ? optimisticTag : tag
      ));
      
      // Update cache
      tagsCache.current[selectedType] = tags.map(tag =>
        tag.id === editingTagId ? optimisticTag : tag
      );
      
      setEditingTagId(null);
      setEditData({ name: '', color: '', description: '' });

      try {
        const response = await axios.patch(
          `${API_URL}/api/tags/${editingTagId}`,
          {
            name: editData.name,
            color: editData.color,
            description: editData.description
          },
          { withCredentials: true }
        );
        
        // Update with server response
        setTags(prev => prev.map(tag =>
          tag.id === editingTagId ? response.data : tag
        ));
        tagsCache.current[selectedType] = tags.map(tag =>
          tag.id === editingTagId ? response.data : tag
        );
      } catch (error) {
        console.error('Failed to update tag:', error);
        // Rollback on error
        setTags(prev => prev.map(tag =>
          tag.id === editingTagId ? oldTag : tag
        ));
        tagsCache.current[selectedType] = tags.map(tag =>
          tag.id === editingTagId ? oldTag : tag
        );
        showToast('error', error.response?.data?.detail || 'Failed to update tag', 'Error');
      }
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingTagId(null);
    setEditData({ name: '', color: '', description: '' });
  };

  // Handle delete
  const handleDelete = (tag) => {
    setTagToDelete(tag);
    setDeleteDialogOpen(true);
  };

  // Confirm delete with optimistic update
  const confirmDelete = async () => {
    if (tagToDelete) {
      const deletedTag = tagToDelete;
      
      // Optimistic delete
      setTags(prev => prev.filter(tag => tag.id !== tagToDelete.id));
      tagsCache.current[selectedType] = tags.filter(tag => tag.id !== tagToDelete.id);
      setDeleteDialogOpen(false);
      setTagToDelete(null);

      try {
        await axios.delete(`${API_URL}/api/tags/${deletedTag.id}`, {
          withCredentials: true
        });
      } catch (error) {
        console.error('Failed to delete tag:', error);
        // Rollback on error
        setTags(prev => [...prev, deletedTag].sort((a, b) => a.name.localeCompare(b.name)));
        tagsCache.current[selectedType] = [...tags, deletedTag].sort((a, b) => a.name.localeCompare(b.name));
        showToast('error', 'Failed to delete tag', 'Error');
      }
    }
  };

  // Handle add new tag with optimistic update
  const handleAddTag = async (newTagData) => {
    // Create optimistic tag
    const optimisticTag = {
      id: `temp-${Date.now()}`,
      name: newTagData.name,
      tag_type: selectedType,
      color: newTagData.color,
      description: newTagData.description,
      usage_count: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Optimistic add
    setTags(prev => [...prev, optimisticTag]);
    tagsCache.current[selectedType] = [...tags, optimisticTag];

    try {
      const response = await axios.post(
        `${API_URL}/api/tags`,
        {
          name: newTagData.name,
          tag_type: selectedType,
          color: newTagData.color,
          description: newTagData.description
        },
        { withCredentials: true }
      );
      
      // Replace optimistic tag with real one
      setTags(prev => prev.map(tag => 
        tag.id === optimisticTag.id ? response.data : tag
      ));
      tagsCache.current[selectedType] = tags.map(tag =>
        tag.id === optimisticTag.id ? response.data : tag
      );
    } catch (error) {
      console.error('Failed to create tag:', error);
      // Rollback on error
      setTags(prev => prev.filter(tag => tag.id !== optimisticTag.id));
      tagsCache.current[selectedType] = tags.filter(tag => tag.id !== optimisticTag.id);
      showToast('error', error.response?.data?.detail || 'Failed to create tag', 'Error');
      throw error;
    }
  };

  const currentTypeConfig = TAG_TYPES.find(t => t.value === selectedType);
  const TypeIcon = currentTypeConfig?.icon || TagIcon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-6"
    >
      {/* Header with Tab Switcher */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-semibold text-zinc-100">Tags Management</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {filteredTags.length} tag{filteredTags.length !== 1 ? 's' : ''}
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
          </div>
        </div>

        {/* Tab Switcher - Underline Style */}
        <div className="flex items-center gap-8 border-b border-white/10">
          {TAG_TYPES.map((type) => {
            const Icon = type.icon;
            const isActive = selectedType === type.value;
            return (
              <button
                key={type.value}
                onClick={() => {
                  setSelectedType(type.value);
                  setSearchQuery('');
                }}
                className="relative pb-3 group"
              >
                <div className={`
                  flex items-center gap-2 text-sm font-medium transition-colors duration-200
                  ${isActive 
                    ? 'text-zinc-100' 
                    : 'text-zinc-500 hover:text-zinc-300'
                  }
                `}>
                  <Icon size={16} />
                  <span className="hidden sm:inline">{type.label}</span>
                  <span className="sm:hidden">{type.value.charAt(0).toUpperCase() + type.value.slice(1)}</span>
                </div>
                
                {/* Underline indicator */}
                <motion.div
                  initial={false}
                  animate={{
                    scaleX: isActive ? 1 : 0,
                    opacity: isActive ? 1 : 0
                  }}
                  transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 origin-left"
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <div className="bg-[#0f1115] border border-white/6 rounded-lg p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder={`Search ${selectedType} tags...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-[#0a0a0a] border-white/10 text-zinc-200 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Tags Grid */}
      {loading ? (
        <div className="bg-[#0f1115] border border-white/6 rounded-lg p-12 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-3"></div>
          <p className="text-zinc-500 text-sm">Loading tags...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredTags.map((tag) => (
            <TagCard
              key={tag.id}
              tag={tag}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isEditing={editingTagId === tag.id}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
              editData={editData}
              setEditData={setEditData}
            />
          ))}
          
          {/* Add Tag Card - always at the end */}
          <AddTagCard onAdd={handleAddTag} />
        </div>
      )}

      {/* Empty State */}
      {filteredTags.length === 0 && searchQuery && (
        <div className="bg-[#0f1115] border border-white/6 rounded-lg p-12 text-center">
          <TagIcon size={40} className="mx-auto text-zinc-600 mb-3" />
          <h3 className="text-zinc-300 font-medium mb-1">No tags found</h3>
          <p className="text-zinc-500 text-sm">
            No tags match your search &ldquo;{searchQuery}&rdquo;
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="text-blue-400 hover:text-blue-300 text-sm mt-3"
          >
            Clear search
          </button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        tag={tagToDelete}
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setTagToDelete(null);
        }}
        onConfirm={confirmDelete}
      />
    </motion.div>
  );
};

export default SegmentsTags;
